import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import UserAgreementService from '../user/common/services/UserAgreementService';

const AgreementModal = ({ 
  show, 
  onHide, 
  trigger, 
  userType = 'buyer',
  triggerContext = {},
  onComplete 
}) => {
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [acceptedDocuments, setAcceptedDocuments] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  useEffect(() => {
    if (show && trigger) {
      fetchRequiredDocuments();
    }
  }, [show, trigger, userType]);

  const fetchRequiredDocuments = async () => {
    try {
      setLoading(true);
      const response = await UserAgreementService.getRequiredDocuments(trigger, userType);
      if (response.success) {
        setRequiredDocuments(response.data || []);
        // Initialize acceptance state
        const initialAccepted = {};
        response.data.forEach(doc => {
          initialAccepted[doc._id] = false;
        });
        setAcceptedDocuments(initialAccepted);
      }
    } catch (error) {
      console.error('Error fetching required documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentAcceptance = (documentId, accepted) => {
    setAcceptedDocuments(prev => ({
      ...prev,
      [documentId]: accepted
    }));
  };

  const handleAcceptAll = async () => {
    try {
      setSubmitting(true);
      
      const agreements = requiredDocuments.map(doc => ({
        documentId: doc._id,
        documentCategory: doc.category,
        documentVersion: doc.version,
        trigger: trigger,
        acceptanceMethod: 'checkbox'
      }));

      const response = await UserAgreementService.acceptAgreements(agreements, triggerContext);
      
      if (response.success) {
        onComplete && onComplete();
        onHide();
      }
    } catch (error) {
      console.error('Error accepting agreements:', error);
      alert('Failed to accept agreements. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const allDocumentsAccepted = requiredDocuments.every(doc => 
    acceptedDocuments[doc._id] === true
  );

  const getTriggerTitle = () => {
    const titles = {
      signup: 'Account Registration',
      service_creation: 'Service Creation',
      order_placement: 'Order Placement',
      order_acceptance: 'Order Acceptance',
      payment_processing: 'Payment Processing',
      seller_verification: 'Seller Verification'
    };
    return titles[trigger] || 'Agreement Required';
  };

  const getTriggerMessage = () => {
    const messages = {
      signup: 'Before creating your account, please review and accept our terms.',
      service_creation: 'Before creating your service, please accept the seller terms and policies.',
      order_placement: 'Before placing your order, please confirm your acceptance of the terms.',
      order_acceptance: 'Before accepting this order, please review the terms.',
      payment_processing: 'Before processing payment, please confirm the terms.',
      seller_verification: 'To complete seller verification, please accept the seller agreements.'
    };
    return messages[trigger] || 'Please review and accept the required agreements.';
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-file-contract me-2"></i>
            {getTriggerTitle()} - Legal Agreements
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Loading required agreements...</div>
            </div>
          ) : (
            <>
              <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                {getTriggerMessage()}
              </Alert>

              <div className="agreements-list">
                {requiredDocuments.map((document, index) => (
                  <div key={document._id} className="agreement-item mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">{document.title}</h6>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openDocument(document)}
                      >
                        <i className="fas fa-eye me-1"></i>
                        View Document
                      </Button>
                    </div>
                    
                    <div className="agreement-preview bg-light p-2 rounded mb-2">
                      <small className="text-muted">
                        Category: {document.category?.replace('_', ' ')} | Version: {document.version}
                      </small>
                      <div 
                        className="mt-1"
                        dangerouslySetInnerHTML={{ 
                          __html: document.content?.substring(0, 150) + '...' 
                        }} 
                      />
                    </div>

                    <Form.Check
                      type="checkbox"
                      id={`agreement-${document._id}`}
                      checked={acceptedDocuments[document._id] || false}
                      onChange={(e) => handleDocumentAcceptance(document._id, e.target.checked)}
                      label={
                        <span>
                          I have read and agree to the{' '}
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-underline"
                            onClick={() => openDocument(document)}
                          >
                            {document.title}
                          </button>
                        </span>
                      }
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAcceptAll}
            disabled={!allDocumentsAccepted || submitting || loading}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Accept All & Continue
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal 
        show={showDocumentModal} 
        onHide={() => setShowDocumentModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedDocument?.title}
            <small className="text-muted ms-2">
              Version {selectedDocument?.version}
            </small>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedDocument && (
            <div 
              className="document-content"
              dangerouslySetInnerHTML={{ 
                __html: selectedDocument.content 
              }} 
            />
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDocumentModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AgreementModal;